import type {IPublishParams} from '@stomp/stompjs';
import {createChatWorker} from './createChatWorker';
import {v4 as uuidv4} from 'uuid';
import type {ChatFrame, ConnectionStart, WorkerCommand, WorkerEvent} from './connectionTypes';

type Config = ConnectionStart & {
    onConnect: () => void;
    onWebSocketClose: (event?: {fatal?: boolean}) => void;
};

export class ChatWorkerClient {
    active = false;
    connected = false;
    private worker?: Worker;
    private callbacks = new Map<string, (frame: ChatFrame) => void>();
    private pending = new Map<number, {resolve: () => void; reject: (error: Error) => void}>();
    private sequence = 0;
    private stopping?: Promise<void>;
    private finishStop?: () => void;
    private suspended = false;
    private presenceId = uuidv4();
    private hasJoined = false;
    private leaveRequested = false;

    private suspend = () => {
        if (!this.active || this.suspended) return;
        this.suspended = true;
        this.connected = false;
        this.rejectPending();
        // A message to a frozen Worker may never run. Termination also tears
        // down its WebSocket and cancels reconnect timers synchronously.
        this.worker?.terminate();
        this.worker = undefined;
        this.config.onWebSocketClose();
    };

    private resume = () => {
        if (!this.active || !this.suspended || document.visibilityState !== 'visible') return;
        this.suspended = false;
        try {
            this.startWorker();
        } catch {
            this.cleanup();
            this.config.onWebSocketClose({fatal: true});
        }
    };

    constructor(private config: Config) {}

    activate() {
        if (this.active) return;
        this.active = true;
        this.suspended = false;
        this.stopping = undefined;
        this.finishStop = undefined;
        window.addEventListener('pagehide', this.suspend);
        window.addEventListener('pageshow', this.resume);
        document.addEventListener('freeze', this.suspend);
        document.addEventListener('resume', this.resume);
        document.addEventListener('visibilitychange', this.resume);
        try {
            this.startWorker();
        } catch (error) {
            this.cleanup();
            throw error;
        }
    }

    private startWorker() {
        const worker = createChatWorker();
        this.worker = worker;
        worker.onmessage = ({data}: MessageEvent<WorkerEvent>) => {
            if (this.worker !== worker) return;
            if (data.type === 'stopped') {
                this.cleanup();
                return;
            }
            if (!this.active) return;
            switch (data.type) {
                case 'connected':
                    this.hasJoined = true;
                    this.connected = true;
                    this.config.onConnect();
                    break;
                case 'message':
                    this.callbacks.get(data.destination)?.({body: data.body, headers: data.headers});
                    break;
                case 'published':
                    this.pending.get(data.id)?.resolve();
                    this.pending.delete(data.id);
                    break;
                case 'publish-error':
                    this.pending.get(data.id)?.reject(new Error(data.error));
                    this.pending.delete(data.id);
                    break;
                case 'closed':
                    this.connected = false;
                    this.rejectPending();
                    console.info('Chat socket closed', {code: data.code, reason: data.reason, wasClean: data.wasClean});
                    this.config.onWebSocketClose();
                    break;
                case 'diagnostic':
                    console.info('Chat connection diagnostic', data.reason);
                    break;
            }
        };
        worker.onerror = event => {
            event.preventDefault();
            if (this.worker !== worker) return;
            this.cleanup();
            this.config.onWebSocketClose({fatal: true});
        };
        worker.onmessageerror = () => {
            if (this.worker !== worker) return;
            this.cleanup();
            this.config.onWebSocketClose({fatal: true});
        };
        const {brokerURL, historyDestination, joinMessage} = this.config;
        try {
            this.post({type: 'start', brokerURL, historyDestination,
                joinMessage: {...joinMessage, presenceId: this.presenceId}, resume: this.hasJoined});
        } catch (error) {
            this.cleanup();
            throw error;
        }
    }

    subscribe(destination: string, callback: (frame: ChatFrame) => void) {
        this.callbacks.set(destination, callback);
    }

    publish(params: IPublishParams): Promise<void> {
        if (!this.connected || !this.active) return Promise.reject(new Error('Chat connection is closed'));
        const id = ++this.sequence;
        return new Promise((resolve, reject) => {
            this.pending.set(id, {resolve, reject});
            try {
                this.post({type: 'publish', id, params});
            } catch (error) {
                this.pending.delete(id);
                reject(error);
            }
        });
    }

    deactivate(): Promise<void> {
        if (this.stopping) return this.stopping;
        this.requestDeparture();
        this.unbindLifecycle();
        this.active = false;
        this.connected = false;
        this.callbacks.clear();
        this.rejectPending();
        if (!this.worker) return Promise.resolve();
        this.stopping = new Promise(resolve => { this.finishStop = resolve; });
        try {
            this.post({type: 'stop'});
        } catch {
            this.cleanup();
        }
        return this.stopping;
    }

    private post(command: WorkerCommand) {
        this.worker!.postMessage(command);
    }

    private requestDeparture() {
        if (this.leaveRequested) return;
        this.leaveRequested = true;
        // Use HTTP so an explicit close works even while disconnected/frozen.
        // Lifecycle suspension never calls this method.
        void fetch('/api/chat/leave', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({uuid: this.config.joinMessage.uuid, presenceId: this.presenceId}),
            keepalive: true,
        }).then(response => {
            if (!response.ok) console.warn('Chat departure was not accepted');
        }).catch(() => console.warn('Chat departure could not be delivered'));
    }

    private rejectPending() {
        this.pending.forEach(pending => pending.reject(new Error('Chat connection is closed')));
        this.pending.clear();
    }

    private cleanup() {
        this.unbindLifecycle();
        this.active = false;
        this.connected = false;
        this.rejectPending();
        this.callbacks.clear();
        this.worker?.terminate();
        this.worker = undefined;
        this.finishStop?.();
    }

    private unbindLifecycle() {
        window.removeEventListener('pagehide', this.suspend);
        window.removeEventListener('pageshow', this.resume);
        document.removeEventListener('freeze', this.suspend);
        document.removeEventListener('resume', this.resume);
        document.removeEventListener('visibilitychange', this.resume);
    }
}
